// Turns a code map into the shared diagram's cards and lines: one card per file, a section per type
// declared in it with a row per member, call lines from member to member, inheritance lines, and
// "uses" lines between files that use each other's types in other ways. Test files are left out,
// drawn faintly or drawn like the rest; rows count the tests that reach their member.
import { TOP_LEVEL_MAIN, type CodeHandler, type CodeMap, type CodeMember, type CodeMemberKind, type CodeType } from '@modelviewer/core';
import { approximateMeasure, sizeNode, type EntityNodeData, type Graph, type GraphEdge, type Line, type Measure, type MemberIcon, type PropertyMode } from '../diagram/graph';
import { codeIndex, idLabel } from './codeIndex';
import type { TestFiles } from './settings';

export interface CodeGraphOptions {
  /** `all`: every member; `keys` ("Linked"): members with a line; `none` ("Types"): types only. */
  members: PropertyMode;
  /** Calls between members of the same file, drawn as loops on its card. */
  insideFile: boolean;
  /** Reads and writes of properties and fields, besides calls. */
  access: boolean;
  /** Calls guessed by name. */
  guesses: boolean;
  /** Files using another file's types other than by calling or inheriting. */
  uses: boolean;
  /** Test files (`CodeFile.test`): left out, drawn faintly, or like the rest. */
  tests: TestFiles;
  /** How many tests reach each member, shown on its row. */
  testCounts?: Map<string, number>;
  measure?: Measure;
}

export const MEMBER_ICON: Record<CodeMemberKind, MemberIcon> = {
  method: 'method',
  constructor: 'constructor',
  destructor: 'method',
  operator: 'operator',
  conversion: 'operator',
  property: 'property',
  indexer: 'property',
  event: 'event',
  field: 'field',
  const: 'const',
  enumMember: 'enumMember',
};

/** `class`, `interface`, `record struct`...: shown beside a type's name. */
function typeKeyword(t: CodeType): string {
  if (t.kind === 'record' && t.modifiers.includes('struct')) return 'record struct';
  return t.kind;
}

function typeName(t: CodeType): string {
  return t.typeParams.length ? `${t.name}<${t.typeParams.join(', ')}>` : t.name;
}

/** A member's row text: `PlaceAsync(Customer, …)` for long signatures. */
export function rowName(m: CodeMember): string {
  if (m.name === TOP_LEVEL_MAIN) return 'top-level statements';
  const s = m.signature;
  if (s.length <= 44 || !s.includes('(')) return s;
  return `${s.slice(0, s.indexOf('('))}(…)`;
}

const fileName = (path: string) => path.slice(path.lastIndexOf('/') + 1);
const folderOf = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '(root)');

/** The test files of a code map. */
export function testFilesOf(map: CodeMap): Set<string> {
  return new Set(map.files.filter((f) => f.test).map((f) => f.path));
}

/** What the code map's status counts: files and calls, leaving out test files while they are hidden. */
export function codeCounts(map: CodeMap, tests: TestFiles): { files: number; hiddenTests: number; calls: number; guessed: number } {
  const { stats } = map;
  if (tests !== 'hide') return { files: stats.files, hiddenTests: 0, calls: stats.exact + stats.guess, guessed: stats.guess };
  const index = codeIndex(map);
  const testFiles = testFilesOf(map);
  let calls = 0;
  let guessed = 0;
  for (const c of map.calls) {
    if (c.kind === 'access' || testFiles.has(index.fileOf(c.from) ?? '') || testFiles.has(index.fileOf(c.to) ?? '')) continue;
    calls++;
    if (c.certainty === 'guess') guessed++;
  }
  return { files: stats.files - testFiles.size, hiddenTests: testFiles.size, calls, guessed };
}

export function buildCodeGraph(map: CodeMap, o: CodeGraphOptions): Graph {
  const index = codeIndex(map);
  const fileOf = index.fileOf;
  const testFiles = testFilesOf(map);
  const hidden = (path: string) => o.tests === 'hide' && testFiles.has(path);
  // Lines to or from a dimmed card are dimmed too.
  const muted = (...paths: string[]) => (o.tests === 'dim' && paths.some((p) => testFiles.has(p)) ? { muted: true } : {});

  // Lines first: they decide which rows the "linked" mode shows.
  const calls = new Map<string, { from: string; to: string; fromFile: string; toFile: string; count: number; exact: boolean }>();
  for (const c of map.calls) {
    // Setting up or checking a mock does not run the member: no line.
    if (c.mock) continue;
    if (c.kind === 'access' && !o.access) continue;
    if (c.certainty === 'guess' && !o.guesses) continue;
    const fromFile = fileOf(c.from);
    const toFile = fileOf(c.to);
    if (!fromFile || !toFile || (fromFile === toFile && !o.insideFile) || hidden(fromFile) || hidden(toFile)) continue;
    const key = `${c.from}->${c.to}`;
    const existing = calls.get(key);
    if (existing) {
      existing.count++;
      existing.exact ||= c.certainty === 'exact';
    } else calls.set(key, { from: c.from, to: c.to, fromFile, toFile, count: 1, exact: c.certainty === 'exact' });
  }
  const blank = { required: false, unique: false, requiredDependent: false, relationship: null };
  const edges: GraphEdge[] = [];
  const linked = new Set<string>();
  for (const [key, c] of calls) {
    const sourceRow = index.members.has(c.from) ? c.from : null;
    const targetRow = index.members.has(c.to) ? c.to : null;
    if (sourceRow) linked.add(sourceRow);
    if (targetRow) linked.add(targetRow);
    edges.push({
      id: `call:${key}`,
      kind: 'call',
      source: c.fromFile,
      target: c.toFile,
      sourceRow,
      targetRow,
      ...blank,
      label: `${idLabel(c.from, index)} → ${idLabel(c.to, index)}${c.count > 1 ? ` ×${c.count}` : ''}`,
      guess: !c.exact,
      count: c.count,
      ...muted(c.fromFile, c.toFile),
    });
  }
  // A value handed to a package goes, probably, to the code's handler of it: a dashed line, with the guesses.
  if (o.guesses && map.handlers.length) {
    const handlers = new Map<string, CodeHandler[]>();
    for (const h of map.handlers) handlers.set(h.type, [...(handlers.get(h.type) ?? []), h]);
    const drawn = new Set<string>();
    for (const h of map.handoffs) {
      const fromFile = fileOf(h.from);
      if (!fromFile || hidden(fromFile)) continue;
      for (const type of h.types) {
        for (const handler of handlers.get(type) ?? []) {
          const toFile = fileOf(handler.member);
          const key = `${h.from}->${handler.member}`;
          if (!toFile || hidden(toFile) || (fromFile === toFile && !o.insideFile) || drawn.has(key)) continue;
          drawn.add(key);
          linked.add(h.from);
          linked.add(handler.member);
          edges.push({
            id: `handled:${key}`,
            kind: 'call',
            source: fromFile,
            target: toFile,
            sourceRow: index.members.has(h.from) ? h.from : null,
            targetRow: handler.member,
            ...blank,
            label: `${idLabel(type, index)} handed to ${h.to}, probably handled by ${idLabel(handler.member, index)}`,
            guess: true,
            ...muted(fromFile, toFile),
          });
        }
      }
    }
  }
  // An HTTP request into an endpoint of the code (an integration test's): to its handler, or the member mapping its lambda.
  const endpointsById = new Map(map.endpoints.map((e) => [e.id, e]));
  for (const r of map.requests) {
    if (r.certainty === 'guess' && !o.guesses) continue;
    const fromFile = fileOf(r.from);
    if (!fromFile || hidden(fromFile)) continue;
    for (const id of r.endpoints) {
      const endpoint = endpointsById.get(id);
      const to = endpoint?.handler ?? endpoint?.mappedIn;
      const toFile = to ? fileOf(to) : undefined;
      if (!to || !toFile || hidden(toFile) || (fromFile === toFile && !o.insideFile)) continue;
      linked.add(r.from);
      linked.add(to);
      edges.push({
        id: `request:${r.from}->${id}`,
        kind: 'call',
        source: fromFile,
        target: toFile,
        sourceRow: index.members.has(r.from) ? r.from : null,
        targetRow: to,
        ...blank,
        label: `${r.method} ${r.url} → ${id}`,
        guess: r.certainty === 'guess',
        ...muted(fromFile, toFile),
      });
    }
  }
  const connected = new Set(edges.map((e) => `${e.source}|${e.target}`));
  const inherits = new Set<string>();
  for (const t of map.types) {
    const from = t.parts[0]?.file;
    for (const sup of [...(t.base ? [t.base] : []), ...t.interfaces]) {
      const to = fileOf(sup);
      if (!from || !to || from === to || inherits.has(`${from}|${to}`) || hidden(from) || hidden(to)) continue;
      inherits.add(`${from}|${to}`);
      edges.push({ id: `inh:${from}->${to}`, kind: 'inheritance', source: from, target: to, sourceRow: null, targetRow: null, ...blank, label: `${t.name} : ${index.types.get(sup)?.name ?? sup}`, ...muted(from, to) });
    }
  }
  if (o.uses) {
    const uses = new Map<string, number>();
    for (const u of map.uses) {
      if (u.kind === 'base' || u.kind === 'interface') continue;
      const from = fileOf(u.from);
      const to = fileOf(u.to);
      if (!from || !to || from === to || hidden(from) || hidden(to)) continue;
      const pair = `${from}|${to}`;
      if (connected.has(pair) || inherits.has(pair)) continue;
      uses.set(pair, (uses.get(pair) ?? 0) + u.count);
    }
    for (const [pair, count] of uses) {
      const [from, to] = pair.split('|') as [string, string];
      edges.push({ id: `uses:${from}->${to}`, kind: 'uses', source: from, target: to, sourceRow: null, targetRow: null, ...blank, label: `${fileName(from)} uses ${fileName(to)}`, count, ...muted(from, to) });
    }
  }

  // Cards: one per file that declares something.
  const nodes: EntityNodeData[] = [];
  for (const file of map.files) {
    if (!file.types.length || hidden(file.path)) continue;
    const lines: Line[] = [];
    const depthOf = (t: CodeType): number => {
      let d = 0;
      for (let outer = t.outer ? index.types.get(t.outer) : undefined; outer && outer.parts.some((p) => p.file === file.path); outer = outer.outer ? index.types.get(outer.outer) : undefined) d++;
      return d;
    };
    const ordered = file.types
      .map((id) => index.types.get(id)!)
      .filter(Boolean)
      .sort((a, b) => (a.parts.find((p) => p.file === file.path)?.line ?? 0) - (b.parts.find((p) => p.file === file.path)?.line ?? 0));
    for (const t of ordered) {
      const depth = depthOf(t);
      lines.push({ kind: 'section', section: { id: `type:${t.id}`, kind: 'type', name: typeName(t), type: typeKeyword(t), collection: false, json: false, optional: false, depth } });
      if (o.members === 'none') continue;
      for (const m of index.membersOf.get(t.id) ?? []) {
        if (m.file !== file.path) continue;
        if (o.members === 'keys' && !linked.has(m.id)) continue;
        lines.push({
          kind: 'row',
          row: {
            id: m.id,
            name: rowName(m),
            type: m.kind === 'constructor' || m.name === TOP_LEVEL_MAIN ? '' : (m.valueType ?? ''),
            optional: false,
            pk: false,
            fk: false,
            ak: false,
            generated: false,
            concurrency: false,
            depth: depth + 1,
            member: MEMBER_ICON[m.kind],
            ...(o.testCounts?.get(m.id) ? { tests: o.testCounts.get(m.id) } : {}),
          },
        });
      }
    }
    nodes.push(
      sizeNode(
        {
          id: file.path,
          key: file.path,
          label: fileName(file.path),
          namespace: file.namespace,
          schema: folderOf(file.path),
          badges: [],
          base: null,
          table: file.path,
          lines,
          anchors: [],
          width: 0,
          height: 0,
          ...muted(file.path),
        },
        o.measure ?? approximateMeasure,
      ),
    );
  }

  // Lines attach to rows that are on their cards, or to the card.
  const rowsOf = new Map(nodes.map((n) => [n.id, new Set(n.lines.flatMap((l) => (l.kind === 'row' ? [l.row.id] : [])))]));
  const shown = edges
    .filter((e) => rowsOf.has(e.source) && rowsOf.has(e.target))
    .map((e) => {
      const sourceRow = e.sourceRow && rowsOf.get(e.source)!.has(e.sourceRow) ? e.sourceRow : null;
      const targetRow = e.targetRow && rowsOf.get(e.target)!.has(e.targetRow) ? e.targetRow : null;
      return sourceRow === e.sourceRow && targetRow === e.targetRow ? e : { ...e, sourceRow, targetRow };
    });
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const e of shown) {
    if (e.sourceRow) byId.get(e.source)!.anchors.push(e.sourceRow);
    if (e.targetRow) byId.get(e.target)!.anchors.push(e.targetRow);
  }
  for (const n of nodes) n.anchors = [...new Set(n.anchors)];
  return { nodes, edges: shown };
}
